/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import heritageData from "~/data/home/heritage.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { cn } from "~/lib/utils";
import { LANG } from "~/utils/constant";
import ClientOnly from "../shared-components/ClentOnly";

type HeritageData = {
  id: number;
  enTitle: string;
  guTitle: string;
  enDescription: string;
  guDescription: string;
  images: string;
  navigateTo: string;
};

type AboutUsItem = {
  item: HeritageData;
  className?: string;
  alignment?: "left" | "center" | "right";
  imageSize?: number;
  cardIndex?: number;
};

function AboutUs() {
  const { LocalizedLink } = useLocale();
  const { medium, normalInter } = useFontStyle();
  const { t, ready } = useTranslation("common");
  const lang = useLanguageParams();
  const isGujarati = lang === LANG.gujarati;

  const AboutUsCard = ({ item, className, alignment, cardIndex }: AboutUsItem) => {
    // Create continuous design flow across cards
    const getBackgroundDesign = () => {
      switch (cardIndex) {
        case 0:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -right-28 w-[200px] h-[200px] opacity-[0.08] object-cover select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-10 -left-10 w-[120px] h-[120px] opacity-[0.08] object-cover rotate-12 select-none"
              />
            </>
          );

        case 1:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -left-[115px] w-[200px] h-[200px] opacity-[0.08] object-cover select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -bottom-[2.75rem] -right-[2.25rem] w-[120px] h-[120px] opacity-[0.08] object-cover -rotate-12 select-none"
              />
            </>
          );

        case 2:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute top-4 -right-[5rem] w-[160px] h-[160px] opacity-[0.08] object-cover rotate-45 select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -bottom-5 -left-8 w-[100px] h-[100px] opacity-[0.08] object-cover select-none"
              />
            </>
          );

        case 3:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute top-4 -left-[5rem] w-[160px] h-[160px] opacity-[0.08] object-cover rotate-45 select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-[3rem] -right-[3rem] w-[120px] h-[120px] opacity-[0.08] object-cover rotate-45 select-none"
              />
            </>
          );

        case 4:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-[3rem] -left-[3rem] w-[120px] h-[120px] opacity-[0.08] object-cover rotate-45 select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -bottom-[2.75rem] -right-[2.25rem] w-[160px] h-[160px] opacity-[0.08] object-cover select-none"
              />
            </>
          );

        default:
          return (
            <>
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute -top-10 -left-10 w-[119px] h-[133px] opacity-[0.08] object-cover select-none"
              />
              <img
                src="/images/heritage_card_design.webp"
                alt="heritage_design"
                className="absolute top-10 -right-32 w-[204px] h-[204px] opacity-[0.08] object-cover select-none"
              />
            </>
          );
      }
    };

    return (
      <LocalizedLink
        to={item.navigateTo}
        className={cn(
          "w-full h-full flex justify-center items-center gap-4 p-4 2xl:p-9 bg-homePageCardColor rounded-[12px] cursor-pointer relative overflow-hidden group ",
          "flex-col min-h-[280px] md:min-h-0",
          className,
        )}
      >
        {getBackgroundDesign()}

        <div
          className={cn(
            "rounded-full bg-slate-300 z-[1] shadow-md shadow-darkOrangeColor/20 flex-shrink-0 ",
            "w-[100px] h-[100px] 2xl:w-[130px] 2xl:h-[130px]",
            alignment === "center" ? "" : "sx:w-[100px] xs:h-[100px]",
          )}
        >
          <img
            src={item.images}
            alt={lang === LANG.english ? item.enTitle : item.guTitle}
            className="w-full h-full object-cover rounded-full select-none"
          />
        </div>

        <div
          className={cn(
            "w-full h-full flex-1 flex flex-col justify-between items-center  text-center",
            // `justify-${alignment || "center"} items-${alignment || "center"}`,
          )}
        >
          <div className={cn("flex flex-col justify-center items-center text-center w-full gap-2")}>
            <p className={cn("body_text_l text-textColor", medium)}>{lang === LANG.english ? item.enTitle : item.guTitle}</p>
            <p className={cn("body_text_m leading-[130%] text-textSecondaryColor", normalInter)}>
              {lang === LANG.english ? item.enDescription : item.guDescription}
            </p>
            <button
              className={cn(
                "w-full text-center body_text_m flex justify-center items-center gap-2 text-textHeadingColor hover:underline font-inter",
                normalInter,
              )}
            >
              {t("common.read_more", { defaultValue: "Read more" })} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </LocalizedLink>
    );
  };

  const AboutUsCardContent = (
    <div className="w-full flex flex-col justify-start items-center">
      <div className="w-full flex flex-col gap-btw-text-and-content justify-center items-center">
        <div className="flex flex-col justify-center items-center gap_btw_title_content">
          <p className={cn(" text-center font-medium capitalize", medium, isGujarati ? "home-page-heading-mar-guj" : "home-page-heading")}>
            {t("home.aboutUs", { defaultValue: "About Us" })}
          </p>
          <span className={cn("sub_desc_text_color body_text_l text-center title_para_width ", normalInter)}>
            {t("home.aboutUsDescription", {
              defaultValue:
                "Who is the Bhagwan Shri Swaminarayan and what is the Swaminarayan Sampraday? Learn more and explore the spiritual activities of Shri Swaminarayan Mandir—Kundaldham and Karelibaug-Vadodara.",
            })}
          </span>
        </div>

        <div className="w-full h-full grid grid-cols-1 md:grid-cols-3  gap-4 2xl:gap-6">
          {heritageData.slice(0, 3).map((item, index) => (
            <AboutUsCard key={index} item={item} className="flex-col h-full" alignment="center" imageSize={122} cardIndex={index + 2} />
          ))}
        </div>
      </div>
    </div>
  );
  return <ClientOnly fallback={AboutUsCardContent}>{AboutUsCardContent}</ClientOnly>;
}

export default AboutUs;
